import React, { FC } from "react";
import { IDamsList, IStateOfKnowledge, dataEntry, IDataTest, ISummarySOK, ISocietalRisk } from "../../types/types";
import { Accordion, AccordionSummary, Grid, Paper } from "@material-ui/core";
import BarChart from "./bar";
import DamHeatMap from "./dam_heat_map";
import { ExpandMore } from "@material-ui/icons";
import Typography from "@material-ui/core/Typography";
import Map from "./map";
import PieChart from "./pie";
import { trafficLight } from "../../theme";
import SoKInfo from "./sok_infobar";
import NoDamInfo from "./no_dam";
import HeatMapTable from "./heat_map_table";

interface ISokProps {
    stateOfKnowledge: IStateOfKnowledge[],
    sokSummary: ISummarySOK[],
    damList: IDamsList[],
    assessments_filtered: number[]
    risk_assessments: ISocietalRisk[],
}


const StateOfKnowledge: FC<ISokProps> = ({ stateOfKnowledge, sokSummary, damList, assessments_filtered, risk_assessments }) => {

    // filter the state of knowledge for the given assessment project ids
    const filtered_state_of_knowledge: Array<IStateOfKnowledge> = []
    const filtered_sok_summary: Array<ISummarySOK> = []
    const filtered_risks: Array<ISocietalRisk> = []

    // for each of the assessment project ids
    const elements = assessments_filtered.map(id => {
        // filter the state of knowledge for the given assessment project id
        const filtered = stateOfKnowledge.filter(s => s.assessment_project_id === id)
        // push the filtered state of knowledge to the filtered state of knowledge array
        filtered.forEach(element => filtered_state_of_knowledge.push(element))

        // filter the summary of state of knowledge for the given assessment project id
        const filtered_sok_summary_element = sokSummary.filter(s => s.assessment_project_id === id)
        // push the filtered summary of state of knowledge to the filtered summary of state of knowledge array
        filtered_sok_summary_element.forEach(element => filtered_sok_summary.push(element))  
        
        // filter the societal risk for the given assessment project id
        const filtered_risk_element = risk_assessments.filter(s => s.assessment_project_id === id)
        // push the filtered societal risk to the filtered societal risk array
        filtered_risk_element.forEach(element => filtered_risks.push(element))

    })


    

    let anyDams = false

    if (filtered_state_of_knowledge.length > 0) {
        anyDams = true
    }



    const groups = Array.from(new Set(filtered_state_of_knowledge.map(
        s => s.assessment_template_detail.template.group.name))).sort()

    const getSubGroups = (category: string): Array<string> => {
        return Array.from(new Set(filtered_state_of_knowledge.filter
            (s => s.assessment_template_detail.template.group.name === category).map(
                s => s.assessment_template_detail.template.name))).sort()
    }

    const assessment_ids = Array.from(new Set(filtered_state_of_knowledge.map(
        s => s.assessment_project_id)))

    const get_group_dam_assessments = (group: string): Array<number> => {
        return assessment_ids.map(a_id => {
            const data = filtered_state_of_knowledge.filter(s => s.assessment_project_id === a_id &&
                s.assessment_template_detail.template.group.name === group)
            // return min adequacy level
            const min_adequacy = Math.max(...data.map(s => s.assessment_template_detail.adequacy.level))
            return min_adequacy

        })
    }

    const get_portfolio_summary = (): Array<number> => {
        
        let adequate = 0
        let quesionable = 0
        let inadequate = 0
        
        filtered_sok_summary.forEach(s => {
            if (s.adequacy.level === 1) {
                adequate++
            } else if (s.adequacy.level === 2) {
                quesionable++
            } else if (s.adequacy.level === 3) {
                inadequate++
            }})

        return [adequate, quesionable, inadequate]
    }

    const getPortfolioSummary = (): IDataTest<dataEntry> => {
        const adequacies = get_portfolio_summary()
        return {
            title: "Portfolio Summary",
            legend_labels: ["Formal\nSFAIRP Assessment", "Preliminary\nSFAIRP Assessment", "Indeterminate\nSFAIRP Assessment"],
            description: [],
            data: [{ name: "Formal", value: [adequacies[0]] },
            { name: "Preliminary", value: [adequacies[1]] },
            { name: "Indeterminate", value: [adequacies[2]] }],
            colour: {
                background: [trafficLight.bgreen, trafficLight.byellow, trafficLight.bred],
                font: [trafficLight.green, trafficLight.yellow, trafficLight.red]
            },
            height: 300,
            radius: '60%'
        }
    }

    const getStateOfKnowledgedataSummary = (): IDataTest<dataEntry> => {
        const sokSummary = groups.map(group => {
            const adequacies = get_group_dam_assessments(group)
            return {
                description: group,
                total_adequate: adequacies.filter(a => a === 1).length,
                total_questionable: adequacies.filter(a => a === 2).length,
                total_inadequate: adequacies.filter(a => a === 3).length,
            }

        })
        return {
            title: "State of Knowledge",
            description: sokSummary.map(s => s.description),
            data: [{ name: "Adequate", value: sokSummary.map(s => s.total_adequate) },
            { name: "Questionable", value: sokSummary.map(s => s.total_questionable) },
            { name: "Inadequate", value: sokSummary.map(s => s.total_inadequate) }],
            colour: {
                background: [trafficLight.bgreen, trafficLight.byellow, trafficLight.bred],
                font: [trafficLight.green, trafficLight.yellow, trafficLight.red]
            },
            height: 300
        }
    }


    const getStateOfKnowledgedata = (category: string): IDataTest<dataEntry> => {

        const assessment_sub_groups = getSubGroups(category);
        // console.log("assessment_sub_groups", assessment_sub_groups)

        const counts = assessment_sub_groups.map(sg => {
            return {
                sub_group: sg,
                total_adequate: filtered_state_of_knowledge.filter(s => s.assessment_template_detail.template.name === sg).filter(
                    s => s.assessment_template_detail.adequacy.level === 1).length,
                total_questionable: filtered_state_of_knowledge.filter(s => s.assessment_template_detail.template.name === sg).filter(
                    s => s.assessment_template_detail.adequacy.level === 2).length,
                total_inadequate: filtered_state_of_knowledge.filter(s => s.assessment_template_detail.template.name === sg).filter(
                    s => s.assessment_template_detail.adequacy.level === 3).length
            }
        })

        return {
            title: category,
            description: assessment_sub_groups,
            data: [{ name: "Adequate", value: counts.map(s => s.total_adequate) },
            { name: "Questionable", value: counts.map(s => s.total_questionable) },
            { name: "Inadequate", value: counts.map(s => s.total_inadequate) }],
            colour: {
                background: [trafficLight.bgreen, trafficLight.byellow, trafficLight.bred],
                font: [trafficLight.green, trafficLight.yellow, trafficLight.red]
            }
        }
    }

    return (
        <>
            <Accordion>
                <AccordionSummary id={"1"} expandIcon={<ExpandMore />}>
                    <Typography variant="h6"> Summary </Typography>
                </AccordionSummary>
                <Typography>
                    <Grid container spacing={1} xl={12} direction="row">



                        {(
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                {anyDams
                                    ? <PieChart data={getPortfolioSummary()} />
                                    : <NoDamInfo />}

                            </Grid>
                        )
                        }


                        {(
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                {anyDams
                                    ? <BarChart data={getStateOfKnowledgedataSummary()} />
                                    : <NoDamInfo />}
                            </Grid>
                        )
                        }
                        <SoKInfo />
                    </Grid>
                </Typography>
            </Accordion>


            <Accordion>
                <AccordionSummary id={"2"} expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Category</Typography>
                </AccordionSummary>
                <Typography>
                    <Grid container spacing={3} xl={12} direction="row">
                        {groups.map((template_group_name, index) => {
                            return (
                                <Grid item xs={12} sm={12} md={6} lg={6}>
                                    {anyDams && <BarChart data={getStateOfKnowledgedata(template_group_name)} />}
                                </Grid>
                            )
                        }
                        )
                        }
                        {!anyDams && <Grid item xs={12} sm={12} md={12} lg={12}><NoDamInfo /></Grid>}
                    </Grid>
                </Typography>
            </Accordion>
            <Accordion>
                <AccordionSummary id={"2"} expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Detail</Typography>
                </AccordionSummary>
                <Typography>
                    <Grid container spacing={3} xl={12} direction="row">
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                            {anyDams
                                ? <HeatMapTable stateOfKnowledge={filtered_state_of_knowledge} damList={damList} risk_assessments={filtered_risks} />
                                : <NoDamInfo />}

                        </Grid>
                    </Grid>
                </Typography>
            </Accordion>
        </>
    );
}

export default StateOfKnowledge;
